import React, { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import FAQComponentForm from "./FAQComponentForm";
import ProjectAPI from "../../api/ProjectAPI";
import Breadcrumbs from "../../components/utils/Breadcrumbs";
import ContainerHeader from "../../components/utils/ContainerHeader";
import { COMPONENT_TYPES } from "../../constants/componentType";

import type { ChangeEvent, JSX } from "react";

import type {
  FAQComponent,
  GetProjectPageComponentListResponse,
  GetProjectPageResponse,
} from "../../api/ProjectAPI.types";
import type { PlatformRoutesParams } from "../../components/routes/PlatformRoutes";
import type { AppState } from "../../reducers";

const { FAQ } = COMPONENT_TYPES;

function isNewComponent(
  component: FAQComponent | undefined,
): component is FAQComponent {
  return component?.uuid === "new";
}

function isExistingComponent(
  component: FAQComponent | undefined,
): component is FAQComponent & { uuid: string } {
  return component?.uuid !== undefined && component.uuid !== "new";
}

export default function FAQComponentPage(): JSX.Element {
  const { projectSlug, platform, componentId, pageId } =
    useParams<PlatformRoutesParams>();
  const isNew = componentId === "new";
  const language = useSelector((state: AppState) => state.variableReducer.lang);

  const getProjectPageComponentListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPageComponentList,
    params: [{ pageId, platform, projectSlug }],
    options: { enabled: isNew },
  });

  const getComponentQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getComponent<FAQComponent>,
    params: [{ projectSlug, componentId }],
    options: {
      enabled: !isNew,
    },
  });

  const { data: FAQData } = useQuery(getComponentQuery);

  const { data: projectPageComponentListData } = useQuery({
    ...getProjectPageComponentListQuery,
    select: (data) => data.components,
  });

  const getProjectPageQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPage,
    params: [{ pageId, projectSlug }],
    options: {
      enabled: isNew,
    },
  });

  const { data: projectPageData } = useQuery(getProjectPageQuery);

  const queryClient = useQueryClient();

  const history = useHistory();

  const { t } = useTranslation("editor-platform");

  const [faqComponent, setFAQComponent] = useState<FAQComponent>();

  useEffect(() => {
    if (isNew) {
      const NewFAQ: FAQComponent = {
        storefront_project_page_uuid: pageId,
        dynamic_component: null,
        static_component: null,
        section: "page",
        type: FAQ,
        platform,
        uuid: "new",
        label: t("faqComponentPage.new"),
        enabled: true,
        rank:
          projectPageComponentListData?.length !== undefined
            ? projectPageComponentListData.length + 1
            : 0,
        params: undefined,
        translations: [
          {
            language,
            default: true,
            title: "",
            faq_questions: [],
            cta: {
              label: "",
              subtext: "",
              url: "",
            },
          },
        ],
        translations_indexes: {
          default: {
            true: [0],
          },
          language: {
            [language]: [0],
          },
        },
      };

      setFAQComponent(NewFAQ);
    } else if (FAQData) {
      setFAQComponent(FAQData);
    }
  }, [
    FAQData,
    platform,
    t,
    language,
    isNew,
    pageId,
    projectPageComponentListData?.length,
  ]);

  const breadcrumbs = [
    {
      name: t("editor-menu:platform"),
      url: "/layout",
    },
    {
      name: t("editor-platform:title.layout"),
      url: "/layout",
    },
    {
      name: t(`editor-platform:page`),
      url: `/layout/pages/${pageId}/${platform}`,
    },
    {
      name: t("faq"),
    },
  ];

  const createComponentMutation = useMutation({
    mutationFn: async (component: FAQComponent) =>
      ProjectAPI.createComponent<FAQComponent>({ component, projectSlug }),
    onSuccess: (response) => {
      const oldPath = history.location.pathname;
      const newPath = oldPath.replace(/\/(new)$/, `/${response.uuid}`);
      history.replace(newPath);

      queryClient.setQueryData(
        getProjectPageComponentListQuery.queryKey,
        (
          oldData?: GetProjectPageComponentListResponse,
        ): GetProjectPageComponentListResponse | undefined => {
          if (!oldData) return undefined;
          return {
            ...oldData,
            components: oldData.components.concat(response),
          };
        },
      );

      const hasPlatformInPage =
        projectPageData?.components_platform?.includes(platform);

      if (!hasPlatformInPage) {
        queryClient.setQueryData<GetProjectPageResponse>(
          getProjectPageQuery.queryKey,
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              components_platform: [
                ...(oldData?.components_platform ?? []),
                platform,
              ],
            };
          },
        );
      }

      toast.success(t("faqComponentPage.createSuccess"));
    },
    onError: (error) => {
      console.error("Error creating new component:", error);
    },
  });

  const updateComponentMutation = useMutation({
    mutationFn: async (component: FAQComponent) =>
      ProjectAPI.updateComponent({
        component,
        projectSlug,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(
        getProjectPageComponentListQuery.queryKey,
        (
          oldData?: GetProjectPageComponentListResponse,
        ): GetProjectPageComponentListResponse | undefined => {
          if (!oldData) return undefined;
          return {
            ...oldData,
            components: oldData.components.map((item) =>
              item.uuid === response.uuid ? response : item,
            ),
          };
        },
      );
      queryClient.setQueryData(getComponentQuery.queryKey, response);

      toast.success(t("faqComponentPage.updateSuccess"));
    },
    onError: (error) => {
      console.error("Error updating component:", error);
    },
  });

  const updateComponentParams = (component: FAQComponent): FAQComponent => {
    const updatedComponent: FAQComponent = {
      ...component,
      params: {
        questionCount: component.translations[0]?.faq_questions?.length ?? 0,
        withTitle: Boolean(component.translations[0]?.title),
        withCTAButton: Boolean(
          component.translations[0]?.cta?.label &&
          component.translations[0]?.cta?.url,
        ),
        withCTASubtitle: Boolean(component.translations[0]?.cta?.subtext),
        has_conditions: component.params?.has_conditions,
        conditional_render: component?.params?.conditional_render?.filter(
          (condition) =>
            !!(condition.type && condition.operator && condition.predicat),
        ),
      },
    };

    return updatedComponent;
  };

  const handleSave = (): void => {
    if (!faqComponent) {
      console.error("FAQ component is not defined");
      return;
    }

    const componentWithParams = updateComponentParams(faqComponent);

    if (isExistingComponent(componentWithParams)) {
      updateComponentMutation.mutate(componentWithParams);
    } else if (isNewComponent(componentWithParams)) {
      createComponentMutation.mutate(componentWithParams);
    }
  };

  const handleChange = <K extends keyof FAQComponent>(
    value: FAQComponent[K],
    field: K,
  ): void => {
    setFAQComponent((prevComponent) => {
      if (!prevComponent) return prevComponent;
      return {
        ...prevComponent,
        [field]: value,
      };
    });
  };

  return (
    <div className="size-full overflow-y-auto p-[3.75rem]">
      <Breadcrumbs items={breadcrumbs} />
      {faqComponent ? (
        <React.Fragment>
          <ContainerHeader
            withBackBtn
            item={faqComponent}
            itemStatus={faqComponent.enabled ? "active" : "stop"}
            onSave={handleSave}
            onChangeLabel={(e: ChangeEvent<HTMLInputElement>["target"]): void =>
              handleChange(e.value, "label")
            }
            onChangeState={(isEnabled: boolean): void =>
              handleChange(isEnabled, "enabled")
            }
          />
          <FAQComponentForm
            faqComponent={faqComponent}
            setFAQComponent={setFAQComponent}
          />
        </React.Fragment>
      ) : null}
    </div>
  );
}
